import { DeleteOutline, Edit } from "@mui/icons-material";
import {
  Box,
  Divider,
  Grid2 as Grid,
  Stack,
  IconButton as MuiIconButton,
  Typography,
} from "@mui/material";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { EConfirmModalType } from "src/shared/components";
import LabeledValue from "src/shared/components/LabeledValue";
import { addressTitles } from "src/shared/constants/address.constants";
import { renderLabeledValues } from "src/shared/helpers/render.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { Can } from "src/shared/lib/rback/Can";
import { IAddressFull } from "src/shared/types/address.types";
import { useDeleteAddressMutation } from "../../model/api/partners";
import { EditAddressModal } from "./EditAddressModal";

export const AddressItem: FC<{
  address: IAddressFull;
  registrationCountryId: number;
}> = ({ address, registrationCountryId }) => {
  const { partner_id } = useParams();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteAddress, { isLoading: isLoadingDelete }] =
    useDeleteAddressMutation();
  const handleDeleteAddress = async (closeModal: () => void) => {
    if (partner_id) {
      try {
        await deleteAddress({
          partnerId: partner_id,
          addressId: address.id,
        }).unwrap();
        closeModal();
      } catch (e) {
        enqueueSnackbar("Произошла ошибка при удалении адреса", {
          variant: "error",
          handleClose: closeSnackbar,
        });
      }
    }
  };
  const { modal, showModal } = useConfirmModal({
    onResolve: ({ closeModal }) => handleDeleteAddress(closeModal),
    confirmModalType: EConfirmModalType.DELETE,
    confirmBtnProps: { isLoading: isLoadingDelete, title: "Удалить" },
    title: "Удалить адрес?",
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        gap: 4,
        pb: 4,
      }}
    >
      <Grid container spacing={4} flex={1}>
        <Grid size={12}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "grey.500",
              }}
            >
              {addressTitles[address.type.value]}
            </Typography>
            {address.type.value !== "legal" && <Divider sx={{ flexGrow: 1 }} />}
          </Stack>
        </Grid>

        {address.type.value !== "postal_additional" && (
          <LabeledValue
            label="Страна"
            value={address.country.rus_country_name}
          />
        )}
        {renderLabeledValues(
          [
            { label: "Область", value: address.region },
            { label: "Район", value: address.area },
            { label: "Город", value: address.city },
            { label: "Адрес", value: address.address },
            { label: "Индекс", value: address.postcode },
          ],
          { breakValues: true },
        )}
      </Grid>
      <Stack
        direction={"row"}
        spacing={1}
        sx={{
          minWidth: 72,
        }}
      >
        <>
          <Can perform={["partners.edit", "partners.edit.team"]}>
            <MuiIconButton
              size="small"
              color="inherit"
              sx={{ minWidth: 0, color: "grey.500" }}
              onClick={() => setIsEditModalOpen(true)}
            >
              <Edit />
            </MuiIconButton>
          </Can>
          {isEditModalOpen && partner_id ? (
            <>
              <EditAddressModal
                open
                onClose={() => setIsEditModalOpen(false)}
                partnerId={partner_id}
                address={address}
                registrationCountryId={registrationCountryId}
              />
            </>
          ) : null}
        </>
        {address.type.value === "postal_additional" && (
          <>
            <Can perform={["partners.edit", "partners.edit.team"]}>
              <MuiIconButton
                size="small"
                color="inherit"
                sx={{ minWidth: 0, color: "grey.600" }}
                onClick={() => showModal()}
              >
                <DeleteOutline color="inherit" />
              </MuiIconButton>
            </Can>
            {modal}
          </>
        )}
      </Stack>
    </Box>
  );
};
